/** @jsx jsx */
import React, { useMemo, type ReactNode, type MouseEvent, useEffect } from 'react';
import { css, jsx } from '@compiled/react';
import toNumber from 'lodash/toNumber';
import { token } from '@atlaskit/tokens';
import { DynamicTableStateless } from '@atlaskit/dynamic-table';
import Pagination from '@atlaskit/pagination';
import { Inline } from '@atlaskit/primitives';
import { useIntl } from '@atlassian/jira-intl/src/index.tsx';
import { SpaStatePageReadyOnUpdate } from '@atlassian/jira-spa-state-controller/src/components/page-ready-state/index.tsx';
import type {
	KnowledgeBaseArticleSearchSortByKey,
	KnowledgeBaseArticleSearchSortOrder,
} from '@atlassian/jira-relay/src/__generated__/viewArticlesTableQuery.graphql';
import { fireScreenAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type { KBHubTableColumn } from '../types.tsx';
import messages from '../messages.tsx';
import { getPageSelectedIndex } from '../utils.tsx';

export type KnowledgeTableGraphqlProp<T> = {
	columns: KBHubTableColumn<T>[];
	sortBy: KnowledgeBaseArticleSearchSortByKey;
	sortOrder: KnowledgeBaseArticleSearchSortOrder;
	loadNext: Function;
	loadPrevious: Function;
	page: number;
	totalSize: number;
	knowledgePieces: T[] | undefined;
	emptyStateComponent: ReactNode;
	noSearchResultsComponent: ReactNode;
	searchQuery: string;
	limit: number;
	nextCursor: string | null;
	prevCursor: string | null;
	categoryId: string;
	onSort: Function;
};

type BaseKnowledgePiece = {
	id: string;
};

export const KnowledgeTableGraphql = <T extends BaseKnowledgePiece>({
	columns,
	totalSize,
	knowledgePieces,
	sortBy,
	sortOrder,
	emptyStateComponent,
	noSearchResultsComponent,
	searchQuery,
	prevCursor,
	nextCursor,
	page,
	categoryId,
	loadNext,
	loadPrevious,
	onSort,
}: KnowledgeTableGraphqlProp<T>) => {
	const intl = useIntl();
	const pageNumber = toNumber(page);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const header = useMemo(() => {
		const headerCells = columns.map((column) => ({
			key: column.key,
			...column.header(intl),
		}));
		return { cells: headerCells };
	}, [columns, intl]);

	const rows = useMemo(() => {
		if (totalSize > 0 && Array.isArray(knowledgePieces)) {
			return knowledgePieces.map((knowledge, index) => {
				// Use the zero padded index as part of the key, manually correcting when sorted in descending order
				const indexKey = (sortOrder === 'DESC' ? knowledgePieces.length - 1 - index : index)
					.toString()
					.padStart((knowledgePieces.length - 1).toString().length, '0');
				const cells = columns.map((column) => ({
					// we use a row cell key unique to the piece of knowledge
					key: `${indexKey}-${column.key}-${knowledge.id}`,
					content: column.row(intl, index, knowledge),
				}));
				return {
					// we use a row key unique to the piece of knowledge
					key: `knowledge-${knowledge.id}`,
					cells,
				};
			});
		}
		return [];
	}, [columns, intl, knowledgePieces, sortOrder, totalSize]);

	const paginationi18n = useMemo(
		() => ({
			prev: intl.formatMessage(messages.paginationPrev),
			next: intl.formatMessage(messages.paginationNext),
			label: intl.formatMessage(messages.paginationLabel),
		}),
		[intl],
	);

	const selectedIndex = getPageSelectedIndex(nextCursor, prevCursor);
	const pagesArray = [
		prevCursor ? pageNumber - 1 : 0,
		pageNumber,
		nextCursor ? pageNumber + 1 : 0,
	].filter((number) => number > 0);
	const currentPageNumber = pagesArray[selectedIndex];
	const onPageChange = (event: MouseEvent<HTMLElement>, newPageNumber: number) => {
		if (newPageNumber !== currentPageNumber) {
			if (currentPageNumber + 1 === newPageNumber && nextCursor) {
				loadNext?.();
			} else if (currentPageNumber - 1 === newPageNumber && prevCursor) {
				loadPrevious?.();
			}
		}
	};

	useEffect(() => {
		fireScreenAnalytics(createAnalyticsEvent({}));
	}, [createAnalyticsEvent]);

	const onPageSort = (sort: { key: string; sortOrder: string }) => {
		if (
			typeof sort === 'object' &&
			sort !== null &&
			typeof sort.key === 'string' &&
			typeof sort.sortOrder === 'string'
		) {
			const sortKey = columns.find((column) => column.key === sort.key)?.sortKey;
			onSort(sortKey, sort.sortOrder);
		}
	};

	const articlePagination = () => (
		<Inline alignInline="center">
			<Pagination
				pages={pagesArray}
				// @ts-expect-error - TS2322 - Type '(event: MouseEvent<HTMLElement>, newPageNumber: number, analyticsEvent?: UIAnalyticsEvent | undefined) => void' is not assignable to type '(event: SyntheticEvent<Element, Event>, page: number, analyticsEvent?: UIAnalyticsEvent | undefined) => void'.
				onChange={onPageChange}
				selectedIndex={selectedIndex}
			/>
		</Inline>
	);

	if (totalSize === 0) {
		return (
			<div css={TableContainer}>
				<DynamicTableStateless
					head={header}
					sortKey={sortBy}
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					sortOrder={sortOrder as 'ASC' | 'DESC'}
					paginationi18n={paginationi18n}
				/>
				{searchQuery === '' ? emptyStateComponent : noSearchResultsComponent}
			</div>
		);
	}

	const isPaginated = (nextCursor || prevCursor) && totalSize > 0;

	return (
		<>
			<div css={TableContainer}>
				<DynamicTableStateless
					head={header}
					rows={rows}
					sortKey={sortBy}
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					sortOrder={sortOrder as 'ASC' | 'DESC'}
					onSort={onPageSort}
					isLoading={totalSize < 0}
					paginationi18n={paginationi18n}
				/>
				{totalSize >= 0 && <SpaStatePageReadyOnUpdate comparer={[categoryId]} />}
			</div>
			{isPaginated && articlePagination()}
		</>
	);
};
const TableContainer = css({
	marginTop: token('space.250'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'thead, tbody, tfoot': {
		borderBottom: `2px solid ${token('color.border')}`,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'th, td': {
		wordBreak: 'break-word',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	td: {
		padding: 0,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	th: {
		paddingTop: token('space.100'),
		paddingRight: 0,
		paddingBottom: token('space.100'),
		paddingLeft: 0,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'td:not(:last-child), th:not(:last-child)': {
		paddingRight: token('space.300'),
	},
});
